import { ChevronRight, FileText, Play } from "lucide-react";

import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models";

import { EncounterEntity } from "@src/features/encounters";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

export type PatientPastVisitRowV2 = {
  id: number;
  startedAt: string;
  lead: string | null;
  agendaItemCount?: number | null;
  medicalCodeCount?: number | null;
  freshnessLabel?: string | null;
};

export interface PatientPastVisitsV2Props {
  encounters: PatientPastVisitRowV2[];
  inProgress?: {
    encounterId: number;
    paused?: boolean;
  } | null;
  onResumeInProgress?: () => void;
  onEncounterSelect?: (encounterId: number) => void;
  className?: string;
}

/**
 * Map a terminal patient encounter list item into the Past Visits row model.
 * Enrichment fields land in Phase B; until then counts may be omitted.
 */
export function toPatientPastVisitRow(
  encounter: EncounterListItem & {
    agendaItemCount?: number | null;
    medicalCodeCount?: number | null;
  },
  extras?: Partial<
    Pick<
      PatientPastVisitRowV2,
      "agendaItemCount" | "medicalCodeCount" | "freshnessLabel"
    >
  >,
): PatientPastVisitRowV2 {
  return {
    id: encounter.id,
    startedAt: encounter.startedAt,
    lead: EncounterEntity.notePreview(encounter),
    agendaItemCount:
      extras?.agendaItemCount ?? encounter.agendaItemCount ?? null,
    medicalCodeCount:
      extras?.medicalCodeCount ?? encounter.medicalCodeCount ?? null,
    freshnessLabel: extras?.freshnessLabel ?? null,
  };
}

function formatVisitDate(startedAt: string): string {
  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) return "Visit";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatVisitTime(startedAt: string): string {
  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function EmptyPastVisits() {
  return (
    <VerticalFlexbox className="items-center px-[18px] py-8 text-center md-stitch:px-6">
      <Flexbox
        alignItems="center"
        justifyContent="center"
        className="mb-3.5 size-11 rounded-xl border border-border bg-surface text-muted-foreground"
      >
        <FileText className="size-5" aria-hidden="true" />
      </Flexbox>
      <h2 className="text-sm font-semibold text-surface-foreground">
        No past visits yet
      </h2>
      <p className="mt-1.5 max-w-[280px] text-[13px] leading-5 text-muted-foreground">
        Visits will show up here once you finish one.
      </p>
    </VerticalFlexbox>
  );
}

function InProgressRow({
  paused = false,
  onResume,
}: {
  paused?: boolean;
  onResume?: () => void;
}) {
  return (
    <li>
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-start gap-3 whitespace-normal rounded-none border-b border-border bg-primary/5 px-[18px] py-3.5 text-left font-normal shadow-none hover:bg-primary/10 hover:text-foreground md-stitch:px-6"
        onClick={onResume}
      >
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-[34px] shrink-0 rounded-[9px] bg-primary/10 text-primary"
        >
          <Play className="size-4" aria-hidden="true" />
        </Flexbox>
        <VerticalFlexbox className="min-w-0 flex-1 gap-0.5">
          <span className="text-sm font-semibold text-primary">
            {paused ? "Visit paused" : "Visit in progress"}
          </span>
          <span className="text-[12.5px] text-muted-foreground">
            Resume recording to continue this visit.
          </span>
        </VerticalFlexbox>
        <span className="shrink-0 text-[13px] font-semibold text-primary">
          Resume
        </span>
      </Button>
    </li>
  );
}

function PastVisitRow({
  encounter,
  onSelect,
}: {
  encounter: PatientPastVisitRowV2;
  onSelect?: (encounterId: number) => void;
}) {
  const dateLabel = formatVisitDate(encounter.startedAt);
  const timeLabel = formatVisitTime(encounter.startedAt);
  const ariaName = [
    dateLabel,
    timeLabel,
    encounter.lead,
    encounter.freshnessLabel,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <li aria-label={ariaName || `Visit ${encounter.id}`}>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "h-auto w-full justify-start gap-3 whitespace-normal rounded-none border-b border-border bg-transparent px-[18px] py-3.5 text-left font-normal shadow-none hover:bg-muted/40 hover:text-foreground last:border-b-0 md-stitch:px-6",
          encounter.freshnessLabel && "bg-emerald-500/5",
        )}
        onClick={() => onSelect?.(encounter.id)}
      >
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-[34px] shrink-0 rounded-[9px] bg-primary/5 text-primary"
        >
          <FileText className="size-4" aria-hidden="true" />
        </Flexbox>
        <VerticalFlexbox className="min-w-0 flex-1">
          <HorizontalFlexbox
            alignItems="center"
            flexWrap="wrap"
            className="gap-x-2 gap-y-1"
          >
            <span className="font-mono text-[12px] font-medium text-surface-foreground">
              {dateLabel}
              {timeLabel ? ` · ${timeLabel}` : ""}
            </span>
            {encounter.freshnessLabel ? (
              <span className="font-mono text-[10px] text-emerald-700">
                {encounter.freshnessLabel}
              </span>
            ) : null}
          </HorizontalFlexbox>
          {encounter.lead ? (
            <p className="mt-1 truncate text-[12.5px] leading-5 text-muted-foreground">
              {encounter.lead}
            </p>
          ) : null}
          <HorizontalFlexbox className="mt-1 gap-2.5">
            {encounter.agendaItemCount != null ? (
              <span className="font-mono text-[10px] text-muted-foreground">
                {encounter.agendaItemCount} agenda items
              </span>
            ) : null}
            {encounter.medicalCodeCount != null ? (
              <span className="font-mono text-[10px] text-muted-foreground">
                {encounter.medicalCodeCount} codes
              </span>
            ) : null}
          </HorizontalFlexbox>
        </VerticalFlexbox>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Button>
    </li>
  );
}

export function PatientPastVisitsV2({
  encounters,
  inProgress = null,
  onResumeInProgress,
  onEncounterSelect,
  className,
}: PatientPastVisitsV2Props) {
  const isEmpty = encounters.length === 0 && !inProgress;

  return (
    <section
      aria-label="Past visits"
      className={cn("min-h-0 flex-1 overflow-y-auto", className)}
    >
      {isEmpty ? (
        <EmptyPastVisits />
      ) : (
        <ul className="divide-y-0">
          {inProgress ? (
            <InProgressRow
              paused={inProgress.paused}
              onResume={onResumeInProgress}
            />
          ) : null}
          {encounters.map((encounter) => (
            <PastVisitRow
              key={encounter.id}
              encounter={encounter}
              onSelect={onEncounterSelect}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
